export interface DsFieldDef {
  key: string;
  label: string;
  unit?: string;
  optionListKey?: string;  // DataService getter name for dropdown resolution
}

export interface DsGroupDef {
  groupLabel: string;
  fields: DsFieldDef[];
  isRepeating?: boolean;
  arrayKey?: string;        // model property name for repeating arrays
  arrayFields?: DsFieldDef[]; // field defs for each array item (type1, type2, etc.)
}

export interface DsTabDef {
  tabIndex: number;
  tabName: string;
  sectionKey: string;       // property name on DataSheetIP / DataSheetSubOrderIP
  items: (DsFieldDef | DsGroupDef)[];
  isStatic?: boolean;       // true for tabs with hardcoded content (no model data)
  staticLines?: string[];   // static text lines for display-only tabs
}

export function isGroupDef(item: DsFieldDef | DsGroupDef): item is DsGroupDef {
  return (item as DsGroupDef).groupLabel !== undefined;
}

// Customer Field Whitelist - Only these fields will be exported for customer
export interface CustomerWhitelistConfig {
  [sectionKey: string]: {
    [label: string]: boolean;
  };
}

//CUSTOMER EXPORT RULES (NEW - CONFIG DRIVEN)
export const CUSTOMER_EXPORT_RULES: any = {

  // Circuit Breaker restriction
  dsCircuitBreakerIP: {
    allowedFields: [
      'Make',
      'Di Electric',
      'Castle Lock Requirement',
      'UV Coil'
    ],

    // ONLY THESE GROUPS WILL BE KEPT
    allowedGroups: {
      'Control Voltage': [
        'Closing coil (Permissible limits- 85% to 110%)',
        'Tripping Coil (Permissible limits- 70% to 110%)',
        'Additional Trip Coil (Permissible limits- 70% to 110%)',
        'Spring Charging motor',
        'UV Coil'
      ],
      'Vaccum Contactor': [
        'Vaccum Contactor',
        'Contactor Type',
        'Control Card Voltage',
        'Latch Type'
      ]
    }
  },

  // Meters restriction
  dsMetersIP: {
    limitRowsToOne: ['Multifunction Meter'],

    allowedGroups: {
      'Multifunction Meter': [
        'Applicability',
        'Meter Communication',
        'Meter Communication Port'
      ],
      'Ammeter/Voltmeter': [
        'Type'
      ]
    }
  }

};

export const CUSTOMER_FIELD_WHITELIST: CustomerWhitelistConfig = {
  // 01. GENERAL
  dsGeneralIP: {
    'Main System': true,
    'System Voltage (~±10%)': true,
    'Rated Voltage': true,
    'Dry Frequency Withstand Voltage': true,
    'Rated Impulse Voltage': true,
    'Frequency': true,
    'Switchboard Current Rating': true,
    'Short Time Current Rating and Duration': true,
    'Dynamic Peak Withstand Current': true,
    'Ambient Temperature for Design': true,
    'Temp Rise Limit': true,
    'Earthing System': true,
    'Altitude': true,
    'Internal Arc Rating and Duration': true,
    'Internal Arc Classification': true,
    'Type of Installation': true,
  },
  // 02. PANEL
  dsPanelIP: {
    'Type of Panel': true,
    'Product Type': true,
    'VCB Type': true,
    'For Enclosure': true,
    'For Within Compartment': true,
    'For LV Compartment Door': true,
    'Front Doors and End Covers': true,
    'Rest of the Panel': true,
    'Castle Lock for Panel Interlocking': true,
    'Sheet Thickness for Enclosure': true,
    'LV Compartment Door': true,
    'Breaker Compartment Door': true,
    'Rear Cover': true,
    'Extensible Both Sides': true,
    'Lifting Arrangement': true,
    'Power Cable': true,
    'Control Cable': true,
    'Bustduct': true,
    'Busduct Entry': true,
    'Feeder Name Applicable for Busduct': true,
    '3 Core Cables': true,
    'Single Core Cables': true,
    'Gas Duct Exhaust': true,
    'Type': true,
    'Feeder rating - Breaker Panel': true,
    'Force Cooling Fan': true,
    'Silver Plating at Joints': true,
  },
  // 03. BUSBARS
  dsBusbarsIP: {
    'Busbar System': true,
    'Main Busbar (Amp)': true,
    'From Main Bus bar to Circuit Breaker - Top Jumper': true,
    'Feeder Connections (In cable chamber- Bottom Jumper/Droppers)': true,
    'Main Busbar and Droppers': true,
    'HT Sleeves (except at joints & tap-offs)': true,
    'HT Sleeves Make': true,
    'Insulation at Bus joints and tap-offs for Bus Bar compartment only': true,
    'Phase Identification at Regular Interval': true,
    'Earth Bus Bar': true,
    'Material': true,
    'Size': true,
  },
  // 04. PAINTING
  dsPaintingIP: {
    'Paint Shade': true,
    'Applicable (Entire SWBD/ Max Panel) for Front Doors and End Covers': true,
    'Type of painting process (Corrosion class)': true,
    'Painting Procedure document Number': true,
    'Paint Finish': true,
    'MIMIC': true,
    'MIMIC V2': true,
  },
  // 05. CONTROL/AUXILIARY VOLTAGE LEVELS
  dsControlAuxIP: {
    'Closing and Tripping Circuit': true,
    'Additional Trip Coil Circuit Applicability': true,
    'Spring Charging Motor': true,
    'Metering': true,
    'Space heater, Panel Illumination': true,
    'Annunciation Circuit': true,
  },
  // 06. CONTROL WIRING
  dsControlWiringIP: {
    'Control Wire Type': true,
    'Applicable Standard': true,
    'Insulation Guide': true,
    'Wire Type': true,
    'CT Circuit': true,
    'PT Circuit': true,
    'DC Control Circuit': true,
    'AC Control Circuit (raw for panel illumination , heater , etc.)': true,
    'AC Control Circuit (UPS)': true,
    'Earthing Wire': true,
    'Hinge Door Earth': true,
    'Main AC/DC Bus Wiring': true,
    'Main DC Circuit': true,
    'Main AC Circuit': true,
    'Ferrulling': true,
    'Ferrulling Material': true,
  },
  // 07. TERMINAL BLOCKS
  dsTerminalBlocksIP: {
    'Terminal Block Material': true,
    'Make': true,
    'Type of Terminal': true,
    'Current Transformer Circuit': true,
    'Potential Transformer Circuit': true,
    'Disconnecting type': true,
    'Non-Disconnecting type': true,
    'Control Circuit': true,
  },
  // 08. CIRCUIT BREAKER
  dsCircuitBreakerIP: {

    'Make': true,
    'Di Electric': true,
    'Castle Lock Requirement': true,
    'UV Coil': true,
    'Control Voltage': true,
    'Vaccum Contactor': true,

    // CONTROL VOLTAGE FIELDS
    'Closing coil (Permissible limits- 85% to 110%)': true,
    'Tripping Coil (Permissible limits- 70% to 110%)': true,
    'Additional Trip Coil (Permissible limits- 70% to 110%)': true,
    'Spring Charging motor': true,
    'Contactor Type': true,
    'Control Card Voltage': true,
    'Latch Type': true

  },
  // 09. CURRENT TRANSFORMER
  dsCurrentTrafoIP: {
    'Type': true,
    'Class of Insulation': true,
    'Instruments Safety Factor (Metering Core)': true,
    'Short Time Current Rating (kA)': true,
    'Short Time Current Duration': true,
  },
  // 10. POTENTIAL TRANSFORMER
  dsPotentialTrafoIP: {
    'Type': true,
    'Mounting Type': true,
    'Class of insulation': true,
  },
  // 11. NUMERICAL RELAY
  dsNumericalRelayIP: {
    'Main Protection Relays': true,
    'Make': true,
    'Type': true,
    'Relay communication Protocol': true,
    'Relay communication port': true,
    'Time Sync. Protocol': true,
    'Coating': true,
    'Arc Protection': true,
  },
  // 12. AUXILIARY RELAY
  dsAuxRelayIP: {
    'TCS for Trip coil 1 (95-1)': true,
    'TCS for Trip coil 2 (95-2)': true,
  },
  // 13. METERS
  dsMetersIP: {
    'Ammeter/Voltmeter': true,
    'Type': true,
    'Multifunction Meter': true,
    'Meter Communication': true,
    'Meter Communication Port': true,
    'Applicability': true
  },
  // 17. EARTHING DEVICES
  dsEarthingDevicesIP: {
    'Earthing Device (Bus Side)': true,
    'Earthing Device (Cable Side)': true,
  },
  // 19. FOUNDATION FRAME
  dsFoundationFrameIP: {
    'Foundation Frame ISMC 75 (Height 40mm)': true,
    'Foundation Frame Coating': true,
  },
  // 20. SWITCHBOARD ACCESSORIES QTY
  dsSwbAccessoriesQtyIP: {
    // 'Panel Key for opening LV compartment': true,
    'Breaker Rackin & Rack out handle': true,
    'Earthing Switch handle if applicable': true,
    'Breaker Service trolley (if Applicable)': true,
  },
  // 21. CODES & STANDARDS
  dsCodesStandardsIP: {
    'IEC 62271-200': true,
    'IEC 62271-100': true,
    'IS 16227-1 / IEC 61869-3': true,
    'IS 16227-2 / IEC 61869-2': true,
    'IS/IEC 60529 (IP Code)': true,
  },
};

export const DS_TAB_CONFIG: DsTabDef[] = [
  // ─── TAB 0: General ───
  {
    tabIndex: 0, tabName: 'General', sectionKey: 'dsGeneralIP',
    items: [
      { key: 'mainSystem', label: 'Main System' },
      { key: 'systemVoltage', label: 'System Voltage (~±10%)', unit: 'kV' },
      { key: 'ratedVoltage', label: 'Rated Voltage', unit: 'kV' },
      { key: 'dryFrqWithstdVolt', label: 'Dry Frequency Withstand Voltage', unit: 'kV' },
      { key: 'ratedImpulseVoltage', label: 'Rated Impulse Voltage', unit: 'kV' },
      { key: 'frequency', label: 'Frequency', unit: 'Hz' },
      { key: 'switchboardCurrRating', label: 'Switchboard Current Rating', unit: 'A' },
      { key: 'shortTimeCurrRating', label: 'Short Time Current Rating and Duration', unit: 'kA-Sec' },
      { key: 'dynamicPeakWithstdCurr', label: 'Dynamic Peak Withstand Current', unit: 'kAp' },
      { key: 'ambTempDesign', label: 'Ambient Temperature for Design', unit: '°C' },
      { key: 'tempRiseLimit', label: 'Temp Rise Limit', optionListKey: 'tempRiseList' },
      { key: 'tempRiseLimitSpecialReq', label: 'Special Requirement' },
      { key: 'earthingSystem', label: 'Earthing System', optionListKey: 'earthingSystemList' },
      { key: 'altitude', label: 'Altitude', unit: 'm' },
      { key: 'internalArcRating', label: 'Internal Arc Rating and Duration', unit: 'kA-Sec' },
      { key: 'internalArcClassification', label: 'Internal Arc Classification' },
      { key: 'typInstallation', label: 'Type of Installation' }
    ]
  },

  // ─── TAB 1: Panel ───
  {
    tabIndex: 1, tabName: 'Panel', sectionKey: 'dsPanelIP',
    items: [
      { key: 'dsPanelType', label: 'Type of Panel' },
      { key: 'prodType', label: 'Product Type' },
      { key: 'vcbModelName', label: 'VCB Model Name', optionListKey: 'vcbModel' },
      { key: 'vcbModelDetail', label: 'VCB Model Details', optionListKey: 'vcbModelDetail' },
      { key: 'vcbType', label: 'VCB Type', optionListKey: 'vcbType' },
      { key: 'vcbPole', label: 'VCB Pole', optionListKey: 'vcbPole' },
      { key: 'rackInOutMech', label: 'Rack-in/Rack-out Mechanism', optionListKey: 'rackInOutMech' },
      // Group: Degree of Protection
      {
        groupLabel: 'Degree of Protection',
        fields: [
          { key: 'forEnclosure', label: 'For Enclosure' },
          { key: 'forWithinComp', label: 'For Within Compartment' },
          { key: 'forLVCompDoor', label: 'For LV Compartment Door', optionListKey: 'lvCompDoor' },
          { key: 'forLVCompDoorDetail', label: 'For LV Compartment Door Details', optionListKey: 'forLVCompDoorDetail' },
        ]
      },
      // Group: Material of Enclosure
      {
        groupLabel: 'Material of Enclosure',
        fields: [
          { key: 'frontDoorEndCover', label: 'Front Doors and End Covers' },
          { key: 'restOfPanel', label: 'Rest of the Panel', optionListKey: 'restOfPanel' },
          { key: 'castleLock', label: 'Castle Lock for Panel Interlocking', optionListKey: 'reqNonReqOptions' },
          { key: 'castleLockPhilosophy', label: 'Castle Lock Philosophy' },
        ]
      },
      { key: 'sheetThickForEnc', label: 'Sheet Thickness for Enclosure', unit: 'mm' },
      { key: 'lvCompDoorV2', label: 'LV Compartment Door', optionListKey: 'lvCompDoorV2' },
      { key: 'breakerCompDoor', label: 'Breaker Compartment Door', optionListKey: 'breakerCompDoor' },
      { key: 'rearCover', label: 'Rear Cover', optionListKey: 'rearCover' },
      { key: 'extBothSides', label: 'Extensible Both Sides', optionListKey: 'yesNoOptions' },
      { key: 'liftArrg', label: 'Lifting Arrangement' },
      // Group: Entry
      {
        groupLabel: 'Entry',
        fields: [
          { key: 'powerCable', label: 'Power Cable', optionListKey: 'powerCable' },
          { key: 'controlCable', label: 'Control Cable', optionListKey: 'controlCable' },
          { key: 'busduct', label: 'Bustduct', optionListKey: 'applicabilityOptions' },
          { key: 'busductEntry', label: 'Busduct Entry', optionListKey: 'busductEntry' },
          { key: 'feederNameAppli', label: 'Feeder Name Applicable for Busduct' },
        ]
      },
      // Group: Material of Construction for Cable Gland Plate
      {
        groupLabel: 'Material of Construction for Cable Gland Plate',
        fields: [
          { key: 'coreCables_3', label: '3 Core Cables' },
          { key: 'singleCoreCables', label: 'Single Core Cables', optionListKey: 'singleCoreCables' },
        ]
      },
      // Group: Gas duct Exhaust
      {
        groupLabel: 'Gas duct Exhaust',
        fields: [
          { key: 'gasductExh', label: 'Gas Duct Exhaust', optionListKey: 'applicabilityOptions' },
          { key: 'exhType', label: 'Type', optionListKey: 'exhType' },
          { key: 'exhTypeDetail', label: 'Details' },
        ]
      },
      // Group: Feeder rating - Breaker Panel + Cable Run Details (repeating)
      {
        groupLabel: 'Feeder rating - Breaker Panel + Cable Run Details',
        fields: [],
        isRepeating: true,
        arrayKey: 'feederRatingBreakerPan',
        arrayFields: [
          { key: 'type1', label: 'Type 1', optionListKey: 'cableRunList' },
          { key: 'type2', label: 'Details' },
          { key: 'type3', label: 'Switching', optionListKey: 'switchingList' },
          { key: 'type4', label: 'Cable Core', optionListKey: 'cableRun' },
          { key: 'type5', label: 'No of Run/Phase' },
          { key: 'type6', label: 'Cable Size' },
          { key: 'type7', label: 'Current Rating', optionListKey: 'feederRatingBP' },
          { key: 'type8', label: 'Panel width', optionListKey: 'feederRatingBPList' },
          { key: 'type9', label: 'Breaker PS', optionListKey: 'dsBreakerPS' },
        ]
      },
      // Group: Force Cooling System
      {
        groupLabel: 'Force Cooling Details',
        fields: [
          { key: 'forceCoolingApplic', label: 'Force Cooling Applicability', optionListKey: 'applicabilityOptions' },
          { key: 'forceCoolingVCBCurr', label: 'VCB Current', optionListKey: 'forceCoolingVCBCurr' },
          { key: 'forceCoolingFan', label: 'Force Cooling Fan', optionListKey: 'applicabilityOptions' },
          { key: 'noOfFans', label: 'No of Fans' },
          { key: 'locationofFans', label: 'Location of Fans', optionListKey: 'locationOfFans' },
        ]
      },
      { key: 'silverPlatingatJoints', label: 'Silver Plating at Joints', optionListKey: 'silverPlt' },
      // Group: Non-Breaker Feeder Type (repeating)
      {
        groupLabel: 'Feeder type - Non-Breaker (BRPT)',
        fields: [],
        isRepeating: true,
        arrayKey: 'feederTypeNonBreaker',
        arrayFields: [
          { key: 'type1', label: 'Type 1', optionListKey: 'feederTypeNBList' },
          { key: 'type2', label: 'Current Rating', optionListKey: 'feederRatingBP' },
          { key: 'type3', label: 'Width' },
        ]
      },
    ]
  },
  // ─── TAB 2: Busbars ───
  {
    tabIndex: 2, tabName: 'Busbars', sectionKey: 'dsBusbarsIP',
    items: [
      { key: 'busBarSystem', label: 'Busbar System' },
      // Group: Rating
      {
        groupLabel: 'Rating',
        fields: [
          { key: 'mainBusCircBreaker', label: 'Main Busbar (Amp)', unit: 'A' },
          { key: 'mainBusCircBreaker', label: 'From Main Bus bar to Circuit Breaker - Top Jumper', optionListKey: 'mainBusbarCB' },
          { key: 'mainBusDropper', label: 'Feeder Connections (In cable chamber- Bottom Jumper/Droppers)', optionListKey: 'feederConn' },
        ]
      },
      // Group: Material
      {
        groupLabel: 'Material',
        fields: [
          { key: 'mainBusDropper', label: 'Main Busbar and Droppers', optionListKey: 'materialMBB' },
        ]
      },
      // Group: Insulation
      {
        groupLabel: 'Insulation',
        fields: [
          { key: 'htSleeves', label: 'HT Sleeves (except at joints & tap-offs)' },
          { key: 'htSleevesMake', label: 'HT Sleeves Make', optionListKey: 'htSleevesMake' },
          { key: 'insulationBusJoint', label: 'Insulation at Bus joints and tap-offs for Bus Bar compartment only' },
        ]
      },
      // Group: Phase Identification
      {
        groupLabel: 'Phase Identification',
        fields: [
          { key: 'phaseIdentification', label: 'Phase Identification at Regular Interval', optionListKey: 'phaseIdentification' },
        ]
      },
      // Group: Earth Bus Bar
      {
        groupLabel: 'Earth Bus Bar',
        fields: [
          { key: 'earthBBMaterial', label: 'Material' },
          { key: 'earthBBSize', label: 'Size', optionListKey: 'earthBBSize' },
          { key: 'earthBBSize_2', label: 'Size Detail', optionListKey: 'earthBBSizeDetail' },
        ]
      },
    ]
  },
  // ─── TAB 3: Painting ───
  {
    tabIndex: 3, tabName: 'Painting', sectionKey: 'dsPaintingIP',
    items: [
      // Group: Paint Shade
      {
        groupLabel: 'Paint Shade',
        fields: [
          { key: 'paintShadeApplic', label: 'Multiple Paint Shade Applicable within SWBD', optionListKey: 'yesNoOptions' },
          { key: 'applicFrntDoorEndCvr', label: 'Applicable (Entire SWBD/ Max Panel) for Front Doors and End Covers', optionListKey: 'applicFrontDrEndCvr' },
          { key: 'applicFrntDoorEndCvrDetail', label: 'Non Standard Shade Details' },
        ]
      },
      // Group: Separate Color Details (repeating)
      {
        groupLabel: 'Seperate Color Details',
        fields: [],
        isRepeating: true,
        arrayKey: 'seperateColor',
        arrayFields: [
          { key: 'type1', label: 'Seperate Color', optionListKey: 'sptColorList' },
          { key: 'type2', label: 'Color Type', optionListKey: 'applicFrontDrEndCvr' },
          { key: 'type3', label: 'Color Details' },
        ]
      },
      { key: 'typOfPaintProc', label: 'Type of painting process (Corrosion class)', optionListKey: 'typPaintProc' },
      { key: 'paintProcDoc', label: 'Painting Procedure document Number', optionListKey: 'paintProcDoc' },
      { key: 'paintFinish', label: 'Paint Finish' },
      { key: 'mimic', label: 'MIMIC', optionListKey: 'mimic' },
      { key: 'mimic_v2', label: 'MIMIC V2', optionListKey: 'mimicV2Options' },
    ]
  },
  // ─── TAB 4: Control/Auxiliary Voltage Levels ───
  {
    tabIndex: 4, tabName: 'Control/Auxiliary Voltage Levels', sectionKey: 'dsControlAuxIP',
    items: [
      { key: 'closeTripCirc', label: 'Closing and Tripping Circuit', optionListKey: 'closeTripCirc' },
      { key: 'addTripCoilCirc', label: 'Additional Trip Coil Circuit Applicability', optionListKey: 'addTripCoil' },
      { key: 'addTripCoilCircDetail', label: 'Add. Trip Coil Applicability for Feeder Details (If partially applicable)' },
      { key: 'springChargingMotor', label: 'Spring Charging Motor' },
      { key: 'annunCircuit', label: 'Annunciation Circuit', optionListKey: 'annunCirc' },
      { key: 'meteringConAux', label: 'Metering', optionListKey: 'meteringConAux' },
      { key: 'spaceHeater', label: 'Space heater, Panel Illumination', optionListKey: 'spaceHeater' },
    ]
  },
  // ─── TAB 5: Control Wiring ───
  {
    tabIndex: 5, tabName: 'Control Wiring', sectionKey: 'dsControlWiringIP',
    items: [
      { key: 'conWireType', label: 'Control Wire Type', optionListKey: 'conWireTyp' },
      { key: 'conWireTypeDetail', label: 'Control Wire Details', optionListKey: 'conWireTypDetail' },
      { key: 'applicStand', label: 'Applicable Standard', optionListKey: 'applicStand' },
      { key: 'insulationGrade', label: 'Insulation Guide', optionListKey: 'insulationGrade' },
      { key: 'wireTypeV2', label: 'Wire Type', optionListKey: 'wireTypV2' },
      { key: 'wireTypeV2Detail', label: 'Wire Type Detail', optionListKey: 'wireTypV2Detail' },
      // Group: Size and colour of wire
      {
        groupLabel: 'Size and colour of wire (Control and Auxiliary Circuits)',
        fields: [
          { key: 'ctCircuit', label: 'CT Circuit', optionListKey: 'ctCirc' },
          { key: 'ctCircuitColor', label: 'CT Circuit Color', optionListKey: 'ctCircColor' },
          { key: 'ptCircuit', label: 'PT Circuit', optionListKey: 'ptCirc' },
          { key: 'ptCircuitColor', label: 'PT Circuit Color', optionListKey: 'ptCircColor' },
          { key: 'dcConCircuit', label: 'DC Control Circuit', optionListKey: 'dcConCirc' },
          { key: 'dcConCircuitColor', label: 'DC Control Circuit Color', optionListKey: 'dcConCircColor' },
          { key: 'acConCircuit', label: 'AC Control Circuit (raw for panel illumination , heater , etc.)', optionListKey: 'acConCirc' },
          { key: 'acConCircuitColor', label: 'AC Control Circuit Color', optionListKey: 'acConCircColor' },
          { key: 'earthWire', label: 'Earthing Wire', optionListKey: 'earthWire' },
          { key: 'earthWireColor', label: 'Earthing Wire Color', optionListKey: 'earthWireColor' },
          { key: 'hingeDoorEarth', label: 'Hinge Door Earth', optionListKey: 'hingeDoorEarth' },
        ]
      },
      // Group: Main AC/DC Bus Wiring
      {
        groupLabel: 'Main AC/DC Bus Wiring',
        fields: [
          { key: 'mainDCCirc', label: 'Main DC Circuit', optionListKey: 'mainDCCirc' },
          { key: 'mainDCCircColor', label: 'Main DC Circuit Color', optionListKey: 'mainDCCircColor' },
          { key: 'mainACCirc', label: 'Main AC Circuit', optionListKey: 'mainACCirc' },
          { key: 'mainACCircColor', label: 'Main AC Circuit Color', optionListKey: 'mainACCircColor' },
        ]
      },
      { key: 'ferrulling', label: 'Ferrulling', optionListKey: 'ferrulling' },
      { key: 'ferrullingMat', label: 'Ferrulling Material', optionListKey: 'ferrullingMat' },
      { key: 'ferrullingMatDetail', label: 'Ferrulling Material Detail' },
    ]
  },
  // ─── TAB 6: Terminal Blocks ───
  {
    tabIndex: 6, tabName: 'Terminal Blocks', sectionKey: 'dsTerminalBlocksIP',
    items: [
      { key: 'termBlockMat', label: 'Terminal Block Material', optionListKey: 'termBlockMat' },
      { key: 'termBlockMake', label: 'Make', optionListKey: 'termBlockMake' },
      { key: 'termBlockMakeDetail', label: 'Make Detail' },
      // Group: Disconnecting Type
      {
        groupLabel: 'Disconnecting Type',
        fields: [
          { key: 'typeOfTermDisc', label: 'Type of Terminal', optionListKey: 'typeOfTermDisc' },
          { key: 'currTrnfCirc', label: 'Current Transformer Circuit' },
          { key: 'potentTrnfCirc', label: 'Potential Transformer Circuit' },
        ]
      },
      // Group: Non-Disconnecting Type
      {
        groupLabel: 'Non-Disconnecting Type',
        fields: [
          { key: 'typeOfTermNonDisc', label: 'Type of Terminal', optionListKey: 'typeOfTermNonDisc' },
          { key: 'termBlockContCirc', label: 'Control Circuit' },
        ]
      },
    ]
  },
  // ─── TAB 7: Circuit Breaker ───
  {
    tabIndex: 7, tabName: 'Circuit Breaker', sectionKey: 'dsCircuitBreakerIP',
    items: [
      { key: 'cbMake', label: 'Make' },
      { key: 'diElect', label: 'Di Electric' },
      { key: 'cbDynamicPeakCurr', label: 'Dynamic Peak Withstand Current', unit: 'kAp' },
      { key: 'ratedSingleCap', label: 'Rated Single Capacitor Bank Breaking Current', unit: 'Amp', optionListKey: 'ratedSingCap' },
      // Group: Opening Time
      {
        groupLabel: 'Opening Time',
        fields: [
          { key: 'maxArcTime', label: 'Maximum Arcing Time at 100% Breaking Capacity', unit: 'msec', optionListKey: 'maxArcTime' },
          { key: 'openingTime', label: 'Opening Time', unit: 'msec', optionListKey: 'openingTime' },
          { key: 'totalBreakTime', label: 'Total Break Time at 100% Breaking Capacity', unit: 'msec', optionListKey: 'totalBreakTime' },
        ]
      },
      { key: 'closingTime', label: 'Closing Time', unit: 'msec', optionListKey: 'closingTime' },
      { key: 'ratedOpSeq', label: 'Rated Operating Sequence', optionListKey: 'ratedOpSeq' },
      { key: 'cbClass', label: 'Circuit Breaker Class', optionListKey: 'cbClass' },
      { key: 'maxDCComp', label: 'Maximum Percentage DC Component', unit: '%', optionListKey: 'maxDCComp' },
      { key: 'maxDCCompDetail', label: 'Maximum Percentage DC Component Detail' },
      { key: 'ratedMkCurr', label: 'Rated Making Current', unit: 'kAp' },
      { key: 'cbFreeFix', label: 'Whether Circuit Breaker is Trip Free or Fixed Trip', optionListKey: 'cbFreeFix' },
      { key: 'cbCastleRockReq', label: 'Castle Lock Requirement', optionListKey: 'yesNoOptions' },
      { key: 'uvCoil', label: 'UV Coil', optionListKey: 'applicabilityOptions_UVCoil' },
      // Group: UV Coil Details (repeating)
      {
        groupLabel: 'UV Coil Details',
        fields: [],
        isRepeating: true,
        arrayKey: 'uvCoilDetail',
        arrayFields: [
          { key: 'type1', label: 'Feeder', optionListKey: 'feederRatingBPList' },
        ]
      },
      // Group: Control Voltage
      {
        groupLabel: 'Control Voltage',
        fields: [
          {
            key: 'cbWattCloseCoil',
            label: 'Closing coil (Permissible limits- 85% to 110%)'
          },
          {
            key: 'cbWattTripCoil',
            label: 'Tripping Coil (Permissible limits- 70% to 110%)'
          },
          {
            key: 'cbWattAddTripCoil',
            label: 'Additional Trip Coil (Permissible limits- 70% to 110%)'
          },
          {
            key: 'cbWattSpringChg',
            label: 'Spring Charging motor'
          },
          {
            key: 'cbConVoltUVCoil',
            label: 'UV Coil',
            optionListKey: 'cbConVoltUVCoil'
          }
        ]
      },
      // Group: Wattage
      {
        groupLabel: 'Wattage (Continious/Inrush)',
        fields: [
          { key: 'cbWattCloseCoil', label: 'Closing coil (Inrush time 100 msec)' },
          { key: 'cbWattTripCoil', label: 'Tripping Coil (Inrush time 100 msec)' },
          { key: 'cbWattAddTripCoil', label: 'Additional Trip Coil' },
          { key: 'cbWattSpringChg', label: 'Spring Charging motor (Inrush time 200 msec)' },
        ]
      },
      // Group: Vacuum Contactor
      {
        groupLabel: 'Vaccum Contactor',
        fields: [
          { key: 'vacContactor', label: 'Vaccum Contactor', optionListKey: 'applicabilityOptions' },
          { key: 'vacContType', label: 'Contactor Type', optionListKey: 'vacContType' },
          { key: 'cbCloseTripVolt', label: 'Control Card Voltage', optionListKey: 'cbCloseTripVolt' },
          { key: 'cbLatchType', label: 'Latch Type', optionListKey: 'cbLatchType' },
        ]
      },
    ]
  },
  // ─── TAB 8: Current Transformer ───
  {
    tabIndex: 8, tabName: 'Current Transformer', sectionKey: 'dsCurrentTrafoIP',
    items: [
      { key: 'currTrafoType', label: 'Type' },
      // Group: CT Mould (repeating)
      {
        groupLabel: 'CT Mould',
        fields: [],
        isRepeating: true,
        arrayKey: 'ctMould',
        arrayFields: [
          { key: 'type1', label: 'CT Mould', optionListKey: 'ctMouldList' },
          { key: 'type2', label: 'CT Mould Details', optionListKey: 'ctMould' },
          { key: 'type3', label: 'CBCT', optionListKey: 'applicabilityOptions' },
          { key: 'type4', label: 'CBCT Ratio', optionListKey: 'ctCbCtRatio' },
          { key: 'type5', label: 'CBCT Type', optionListKey: 'ctCbCtType' },
          { key: 'type6', label: 'CBCT Detail 1' },
          { key: 'type7', label: 'CBCT Detail 2' },
          { key: 'type8', label: 'CBCT ID' },
        ]
      },
      { key: 'insulationCl', label: 'Class of Insulation', optionListKey: 'insulClass' },
      { key: 'instruSafety', label: 'Instruments Safety Factor (Metering Core)', optionListKey: 'instruSafety' },
      { key: 'ctShortTimeCurrDr', label: 'Short Time Current Duration', unit: 'sec', optionListKey: 'ctShortTimeCurrDr' },
      { key: 'ctShortTimeCurrDrDetail', label: 'Sp. Requirement (Note: To mention lower STC acceptable for ≤ ratio)' },
      // Group: CBCT
      {
        groupLabel: 'CBCT',
        fields: [
          { key: 'minPrimBal', label: 'Minimum Primary out of balance current', optionListKey: 'minPrimBalance' },
          { key: 'basicInsuLvl', label: 'Basic Insulation Level (BIL)', optionListKey: 'basicInsuLvl' },
          { key: 'ctCBCT', label: 'CBCT', optionListKey: 'applicabilityOptions' },
          { key: 'ctCBCTRatio', label: 'CBCT Ratio', optionListKey: 'ctCbCtRatio' },
          { key: 'ctCBCTId', label: 'CBCT ID' },
        ]
      },
    ]
  },
  // ─── TAB 9: Potential Transformer ───
  {
    tabIndex: 9, tabName: 'Potential Transformer', sectionKey: 'dsPotentialTrafoIP',
    items: [
      { key: 'potTrafoType', label: 'Type' },
      // Group: Mounting Type (repeating)
      {
        groupLabel: 'Mounting Type',
        fields: [],
        isRepeating: true,
        arrayKey: 'potTrafoMountingType',
        arrayFields: [
          { key: 'type1', label: 'Mounting Type', optionListKey: 'potTrafoMountingTypList' },
          { key: 'type2', label: 'Mounting Type Details', optionListKey: 'potTrafoMountingTyp' },
          { key: 'type3', label: 'ABB Drawing No.' },
        ]
      },
      { key: 'potTrafoInsulationCl', label: 'Class of insulation', optionListKey: 'insulClass' },
      { key: 'basicInsulLvl', label: 'Basic Insulation Level (BIL)' },
    ]
  },
  // ─── TAB 10: Numerical Relay ───
  {
    tabIndex: 10, tabName: 'Numerical Relay', sectionKey: 'dsNumericalRelayIP',
    items: [
      { key: 'mainProtRelay', label: 'Main Protection Relays', optionListKey: 'applicabilityOptions' },
      // Group: Numerical Relay Details
      {
        groupLabel: 'Numerical Relay Details',
        fields: [
          { key: 'numRelayMake', label: 'Make', optionListKey: 'numRelMake' },
          { key: 'numRelayMakeDetail', label: 'Make Detail', optionListKey: 'numRelMakeDetail' },
          { key: 'numRelayMakeDetailOther', label: 'Make Detail Other' },
          { key: 'numRelayType', label: 'Type', optionListKey: 'numRelType' },
          { key: 'relayCommProtcl', label: 'Relay communication Protocol', optionListKey: 'numRelCommPrtcl' },
          { key: 'relayCommPort', label: 'Relay communication port', optionListKey: 'numRelCommPort' },
          { key: 'timeSyncProtCl', label: 'Time Sync. Protocol', optionListKey: 'numRelTimeSync' },
          { key: 'numRelayCoating', label: 'Coating', optionListKey: 'numRelCoating' },
          { key: 'numRelayArcProtc', label: 'Arc Protection', optionListKey: 'applicabilityOptions' },
        ]
      },
      // Group: Transducer Required for RTD (repeating)
      {
        groupLabel: 'Transducer Required for RTD',
        fields: [],
        isRepeating: true,
        arrayKey: 'transducerReqRTD',
        arrayFields: [
          { key: 'type1', label: 'Transducer Required for RTD', optionListKey: 'numRelTransReqRTDList' },
          { key: 'type2', label: 'Details', optionListKey: 'applicabilityOptions' },
        ]
      },
    ]
  },
  // ─── TAB 11: Auxiliary Relay ───
  {
    tabIndex: 11, tabName: 'Auxiliary Relay', sectionKey: 'dsAuxRelayIP',
    items: [
      { key: 'auxRelTripCoil1', label: 'TCS for Trip coil 1 (95-1)', optionListKey: 'auxRelTripCoil1_2' },
      { key: 'auxRelTripCoil2', label: 'TCS for Trip coil 2 (95-2)', optionListKey: 'auxRelTripCoil1_2' },
      { key: 'auxMasterTripRelay', label: 'Master trip relay (86)', optionListKey: 'auxRelTripCoil1_2' },
    ]
  },
  // ─── TAB 12: Meters ───
  {
    tabIndex: 12, tabName: 'Meters', sectionKey: 'dsMetersIP',
    items: [
      // Group: Multifunction Meter (repeating)
      {
        groupLabel: 'Multifunction Meter',
        fields: [],
        isRepeating: true,
        arrayKey: 'multiFuncMeter',
        arrayFields: [
          { key: 'type1', label: 'Multifunction Meter', optionListKey: 'metMultiMeterList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
          { key: 'type3', label: 'Meter Communication', optionListKey: 'metMultiComm' },
          { key: 'type4', label: 'Meter Communication Port', optionListKey: 'metMultiCommPort' },
          { key: 'type5', label: 'Other Remarks' },
          { key: 'type6', label: 'Specific Make', optionListKey: 'metSpecificMake' },
        ]
      },
      // Group: Tri-Vector Meter (repeating)
      {
        groupLabel: 'Tri-Vector Meter',
        fields: [],
        isRepeating: true,
        arrayKey: 'triVectMeter',
        arrayFields: [
          { key: 'type1', label: 'Tri-Vector Meter', optionListKey: 'metMultiMeterList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
          { key: 'type3', label: 'Meter Communication', optionListKey: 'metTriComm' },
          { key: 'type4', label: 'Meter Communication Port', optionListKey: 'metTriCommPort' },
          { key: 'type5', label: 'ABT requirement', optionListKey: 'applicabilityOptions' },
          { key: 'type6', label: 'User Utility with location' },
        ]
      },
      // Group: Ammeter/Voltmeter
      {
        groupLabel: 'Ammeter/Voltmeter',
        fields: [
          { key: 'ammeterType', label: 'Type', optionListKey: 'metAmmType' },
          { key: 'ammeterDisplay', label: 'Display', optionListKey: 'metAmmDisplay' },
        ]
      },
    ]
  },
  // ─── TAB 13: Surge Arrestor ───
  {
    tabIndex: 13, tabName: 'Surge Arrestor', sectionKey: 'dsSurgeArrestorIP',
    items: [
      // Group: Surge Arrestor (repeating)
      {
        groupLabel: 'Surge Arrestor',
        fields: [],
        isRepeating: true,
        arrayKey: 'surgeArr',
        arrayFields: [
          { key: 'type1', label: 'Surge Arrestor', optionListKey: 'metMultiMeterList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
          { key: 'type3', label: 'Class', optionListKey: 'surgeArr' },
        ]
      },
      { key: 'typeDesg', label: 'Type Designation', optionListKey: 'typeDesg' },
      { key: 'disCurrent', label: 'Discharge Current', optionListKey: 'disCurrent' },
    ]
  },
  // ─── TAB 14: Annunciator ───
  {
    tabIndex: 14, tabName: 'Annunciator', sectionKey: 'dsAnnuciatorIP',
    items: [
      // Group: Annunciator (repeating)
      {
        groupLabel: 'Annuciator',
        fields: [],
        isRepeating: true,
        arrayKey: 'annunciatorData',
        arrayFields: [
          { key: 'type1', label: 'Annuciator', optionListKey: 'numRelTransReqRTDList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
          { key: 'type3', label: 'Annunciator Type', optionListKey: 'annunTyp' },
          { key: 'type4', label: 'Communicable', optionListKey: 'yesNoOptions' },
          { key: 'type5', label: 'Communication port', optionListKey: 'annunCommPort' },
        ]
      },
    ]
  },
  // ─── TAB 15: Switches ───
  {
    tabIndex: 15, tabName: 'Switches', sectionKey: 'dsSwitchesIP',
    items: [
      // Group: Breaker Trip - Neutral - Close (T-N-C) Switch
      {
        groupLabel: 'Breaker Trip - Neutral - Close (T-N-C) switch',
        fields: [
          { key: 'breakerTNC', label: 'Breaker Trip - Neutral - Close (T-N-C) switch', optionListKey: 'swBreakTrip' },
          { key: 'breakerTNCDetail', label: 'Type' },
          { key: 'breakrTNCLock', label: 'Lockable', optionListKey: 'yesNoOptions' },
          { key: 'breakrTNCLmd', label: 'Loss Motion device with LMD', optionListKey: 'yesNoOptions' },
        ]
      },
      // Group: Local - Remote Switch
      {
        groupLabel: 'Local - Remote Switch',
        fields: [
          { key: 'remoteLocalSwLock', label: 'Lockable', optionListKey: 'yesNoOptions' },
          { key: 'remoteLocalSwHandle', label: 'Handle Type', optionListKey: 'swRemHandleType' },
        ]
      },
      { key: 'ammSelSwitch', label: 'Ammeter Selector Switch', optionListKey: 'applicabilityOptions' },
      { key: 'voltSelSwitch', label: 'Voltmeter Selector Switch', optionListKey: 'swVoltSelector' },
      { key: 'autoIndepManual', label: 'Auto- Independent - Manual', optionListKey: 'applicabilityOptions' },
      { key: 'tripSelSwitch', label: 'Trip Selector Switch', optionListKey: 'applicabilityOptions' },
    ]
  },
  // ─── TAB 16: Ethernet Switches ───
  {
    tabIndex: 16, tabName: 'Ethernet Switches', sectionKey: 'dsEthernetSwitchesIP',
    items: [
      { key: 'ethSwitches', label: 'Ethernet Switches', optionListKey: 'applicabilityOptions' },
      // Group: Ethernet Switches Details
      {
        groupLabel: 'Ethernet Switches Details',
        fields: [
          { key: 'ethSwMake', label: 'Make' },
          { key: 'ethSwCommArch', label: 'Communication Architecture', optionListKey: 'ethCommArch' },
          { key: 'ethSwCommArchDetails', label: 'Communication Arch Details' },
          { key: 'ethSwRemComm', label: 'Remote Communication (Uplink) Port/Speed', optionListKey: 'ethRemComm' },
          { key: 'ethSwAuxSupply', label: 'Aux. Supply', optionListKey: 'ethAuxSupply' },
          { key: 'ethSwPwrSupply1', label: 'Power Supply  1', optionListKey: 'ethPowerSup1_2' },
          { key: 'ethSwPwrSupply2', label: 'Power Supply  2', optionListKey: 'ethPowerSup1_2' },
          { key: 'ethSwCoating', label: 'Coating', optionListKey: 'ethCoating' },
          { key: 'ethSwMounting', label: 'Mounting', optionListKey: 'ethMounting' },
        ]
      },
    ]
  },
  // ─── TAB 17: Earthing Devices ───
  {
    tabIndex: 17, tabName: 'Earthing Devices', sectionKey: 'dsEarthingDevicesIP',
    items: [
      { key: 'edBus', label: 'Earthing Device (Bus Side)', optionListKey: 'applicabilityOptions' },
      { key: 'edBusDetail', label: 'Earthing Device (Bus Side) Detail', optionListKey: 'erthDeviceBus' },
      { key: 'edCable', label: 'Earthing Device (Cable Side)', optionListKey: 'erthDeviceCable' },
    ]
  },
  // ─── TAB 18: Special Devices ───
  {
    tabIndex: 18, tabName: 'Special Devices', sectionKey: 'dsSpecialDevicesIP',
    items: [
      // Group: Voltage Presence Indicator
      {
        groupLabel: 'Voltage Presence Indicator+Voltage detecting Insulator',
        fields: [
          { key: 'sdVoltIndicator', label: 'Voltage Presence Indicator+Voltage detecting Insulator', optionListKey: 'splDevVolIndicator' },
          { key: 'sdMounting', label: 'Mounting', optionListKey: 'splDevMounting' },
        ]
      },
      { key: 'sdIRWindow', label: 'IR Window', optionListKey: 'applicabilityOptions' },
      { key: 'sdInspecWindow', label: 'Inspection Window', optionListKey: 'applicabilityOptions' },
      // Group: Temperature Monitoring System
      {
        groupLabel: 'Temperature Monitoring System (TMS)',
        fields: [
          { key: 'sdTMS', label: 'Temperature Monitoring System (TMS)', optionListKey: 'splDevTempMSys' },
          { key: 'sdTMSSensorType', label: 'Sensor Type', optionListKey: 'splDevSensorTyp' },
          { key: 'sdTMSComm', label: 'Communication', optionListKey: 'splDevTmsComm' },
        ]
      },
      { key: 'sdHumidityMonitoring', label: 'Humdity Monitoring', optionListKey: 'applicabilityOptions' },
      { key: 'sdHumidityMonitoringMkPhl', label: 'Make / Philosophy' },
      // Group: Temperature Scanner (repeating)
      {
        groupLabel: 'Temperature Scanner',
        fields: [],
        isRepeating: true,
        arrayKey: 'sdTempScanner',
        arrayFields: [
          { key: 'type1', label: 'Temperature Scanner', optionListKey: 'splDevList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
        ]
      },
      // Group: Transducer (parent)
      {
        groupLabel: 'Transducer',
        fields: []
      },
      // Group: Current Transducer (repeating)
      {
        groupLabel: 'Current Transducer',
        fields: [],
        isRepeating: true,
        arrayKey: 'sdCurrTransducer',
        arrayFields: [
          { key: 'type1', label: 'Current Transducer', optionListKey: 'splDevList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
        ]
      },
      // Group: Voltage Transducer (repeating)
      {
        groupLabel: 'Voltage Transducer',
        fields: [],
        isRepeating: true,
        arrayKey: 'sdVoltTransducer',
        arrayFields: [
          { key: 'type1', label: 'Voltage Transducer', optionListKey: 'splDevList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
        ]
      },
      // Group: Power Transducer (repeating)
      {
        groupLabel: 'Power Transducer',
        fields: [],
        isRepeating: true,
        arrayKey: 'sdPowerTransducer',
        arrayFields: [
          { key: 'type1', label: 'Power Transducer', optionListKey: 'splDevList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
        ]
      },
      // Group: Multi Function Transducer (repeating)
      {
        groupLabel: 'Multi Function Transducer',
        fields: [],
        isRepeating: true,
        arrayKey: 'sdMultiFuncTransducer',
        arrayFields: [
          { key: 'type1', label: 'Multi Function Transducer', optionListKey: 'splDevList' },
          { key: 'type2', label: 'Applicability', optionListKey: 'applicabilityOptions' },
        ]
      },
      { key: 'sdRTB', label: 'Relay Test Terminal Block (RTB)', optionListKey: 'applicabilityOptions' },
      { key: 'sdTTB', label: 'Test Terminal Block', optionListKey: 'applicabilityOptions' },
      { key: 'sdTestPlug', label: 'Test plug', optionListKey: 'applicabilityOptions' },
      { key: 'sdSLT', label: 'Shutter Lifting Trolley', optionListKey: 'applicabilityOptions' },
      { key: 'mediaConv', label: 'Media Converter', optionListKey: 'applicabilityOptions' },
    ]
  },
  // ─── TAB 19: Foundation Frame ───
  {
    tabIndex: 19, tabName: 'Foundation Frame', sectionKey: 'dsFoundationFrameIP',
    items: [
      { key: 'ffrISMC', label: 'Foundation Frame ISMC 75 (Height 40mm)', optionListKey: 'applicabilityOptions' },
      { key: 'ffrCoating', label: 'Foundation Frame Coating', optionListKey: 'ffrCoating' },
    ]
  },
  // ─── TAB 20: Switchboard Accessories Quantity ───
  {
    tabIndex: 20, tabName: 'Switchboard Accessories Quantity', sectionKey: 'dsSwbAccessoriesQtyIP',
    items: [
      { key: 'swbPanelKey', label: 'Panel Key for opening LV compartment' },
      { key: 'swbBreakerRackInOut', label: 'Breaker Rackin & Rack out handle' },
      { key: 'swbEarthSwtHandle', label: 'Earthing Switch handle if applicable' },
      { key: 'swbBreakerServiceTrolley', label: 'Breaker Service trolley (if Applicable)' },
      { key: 'swbRelayTest', label: 'Relay Test Plug (If Applicable)' },
      { key: 'swbPTRamp', label: 'PT Ramp', optionListKey: 'applicabilityOptions' },
      // Group: Special Requirements
      {
        groupLabel: 'Special Requirements',
        fields: [
          { key: 'swbSpecialReq', label: 'Special Requirement' },
          { key: 'swbSpecialReqDetails', label: 'Details' },
        ]
      },
      // Group: Additional Project Specific Points (Repeating)
      {
        groupLabel: 'Additional Project Specific Points',
        fields: [],
        isRepeating: true,
        arrayKey: 'additionalPoints',
        arrayFields: [
          { key: 'type1', label: 'Action Item for Pending Points' },
          { key: 'type2', label: 'Who', optionListKey: 'swbActionPoints' },
          { key: 'type3', label: 'When' },
          { key: 'type4', label: 'Remarks' },
        ]
      },
    ]
  },
  // ─── TAB 21: Codes & Standards ───
  {
    tabIndex: 21, tabName: 'Codes & Standards', sectionKey: 'dsCodesStandardsIP',
    isStatic: true,
    staticLines: [
      'IEC 62271-200 :- High-voltage switchgear and controlgear - Part 200: AC metal-enclosed switchgear and controlgear for rated voltages above 1 kV and up to and including 52 kV',
      'IEC 62271-100 :- High-voltage switchgear and controlgear - Part 100: Alternating-current circuit-breakers',
      'IS 16227-1 & 3/2015, IEC 61869-3 :- Potential Transformer',
      'IS 16227-1 & 2/2016, IEC 61869-2 :- Current Transformer',
      'IS/IEC 60529 :- Degrees of Protection Provided by Enclosures (IP Code)',
    ],
    items: []
  },
];
